import { useEffect, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { MAINLAND_UK_DELIVERY_ZONE } from '@noths/polaris-client-localisation';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { RecommendationsTrackingProduct } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { transformRecommendedProductToProductCarouselItem } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { isProd } from 'src/environment';
import { logger } from 'src/utils/serverLogger';

/**
 * Vertex's same-seller model, narrowed further below by a `partner_shortcode`
 * filter so the rail is the seed product's partner's. Named because two decisions
 * key off it: that filter, and whether a card may carry a badge that describes the
 * partner.
 */
const SAME_PARTNER_SERVING_CONFIG = 'pdp_same_seller_2';

const isSinglePartnerRail = (servingConfig: RecommendationsServingConfig) =>
  servingConfig === SAME_PARTNER_SERVING_CONFIG;

const createListingsAPIFilter = (partnerShortCode?: string) => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];

  if (partnerShortCode) {
    const partnerFilter = new ListingsRequestTextFilterBuilder('attributes.partner_shortcode', [
      partnerShortCode,
    ]).build();
    filterCollection.push(partnerFilter);
  }

  return new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
};

export interface UseListingsRecommendationsArgs {
  currencyCode: CurrencyCode;
  deliveryZone?: DeliveryZoneCode;
  partnerShortCode: string;
  productCode: string;
  servingConfig: RecommendationsServingConfig;
  visitorId?: string;
}

type CreateRecommendationsParametersArgs = Pick<
  UseListingsRecommendationsArgs,
  | 'currencyCode'
  | 'deliveryZone'
  | 'productCode'
  | 'servingConfig'
  | 'visitorId'
  | 'partnerShortCode'
> & {
  userId?: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  deliveryZone,
  partnerShortCode,
  productCode,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersArgs): FetchRecommendationsDataArgs => {
  const id = getProductIdFromProductCode(Number(productCode)).toString();
  const requestBuilder = new RecommendationsRequestBuilder();

  requestBuilder.currency(currencyCode);
  /* The zone scopes the `delivery` option the API resolves per product, so it is
     sent on every request rather than applied to the response. Omitting it is not
     neutral: the API reads an absent zone as mainland UK and would quote a non-UK
     customer a UK price. A visitor with no locale header and no `delivery_zone`
     cookie has no zone, which is a real case rather than a gap, and this is the
     one place that decides what that means. Same choice, for the same reason, as
     the PLP grid's `adaptStoreToRequestBody`. */
  requestBuilder.deliveryZone(deliveryZone ?? MAINLAND_UK_DELIVERY_ZONE);
  requestBuilder.pageSize(10);
  requestBuilder.products([{ id }]);

  const andFilter = isSinglePartnerRail(servingConfig)
    ? createListingsAPIFilter(partnerShortCode)
    : createListingsAPIFilter();

  requestBuilder.filter(andFilter);

  const body = requestBuilder.build();

  return {
    body,
    clientSide: true,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: 15000,
    userId,
    visitorId,
  };
};

export const useListingsRecommendations = ({
  currencyCode,
  deliveryZone,
  partnerShortCode,
  productCode,
  servingConfig,
  visitorId,
}: UseListingsRecommendationsArgs) => {
  const [recommendedProducts, setRecommendedProducts] = useState<ProductCarouselItem[] | null>(
    null,
  );
  /**
   * The same response, kept in the shape GA reports on. Both arrays are mapped
   * from `responseData.products` in the same order, so a card's index in the
   * carousel is the index of the service product that produced it, which is how
   * the container resolves a click back to something it can track. They are set
   * and cleared together so they can never disagree.
   */
  const [productsToTrack, setProductsToTrack] = useState<RecommendationsTrackingProduct[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setRecommendedProducts(null);
        setProductsToTrack([]);
        const userAttributes = await getUserAttributes();
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          deliveryZone,
          partnerShortCode,
          productCode,
          servingConfig,
          userId: userAttributes?.sub,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setRecommendedProducts([]);
          setProductsToTrack([]);
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        /* Captured once for the whole response rather than left to the
           adapter's per-call default, so every card in the rail counts its
           arrival from the same instant. Otherwise a rail mapped across midnight
           could show two different dates for the same lead time. */
        const now = new Date();

        setRecommendedProducts(
          responseData.products.map((product) =>
            transformRecommendedProductToProductCarouselItem(product, {
              currency: currencyCode,
              isSinglePartnerRail: isSinglePartnerRail(servingConfig),
              now,
            }),
          ),
        );
        setProductsToTrack(responseData.products);
      } catch (error) {
        setRecommendedProducts([]);
        setProductsToTrack([]);
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [currencyCode, deliveryZone, servingConfig, visitorId, partnerShortCode, productCode]);

  return {
    productsToTrack,
    recommendedProducts,
  };
};
